import * as React from 'react'
import { useState } from 'react'

import { Alert, Box, Link, Section, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import useLegalEntities from 'modules/employees/hooks/useLegalEntities'
import { AssignmentList } from 'modules/finance/components/CostCenterAssigments/Assigments/AssignmentList'
import { DeleteCostCenterAssignmentModal } from 'modules/finance/components/CostCenterAssigments/Assigments/DeleteModal'
import { CostCenterAssignmentsSidepanel } from 'modules/finance/components/CostCenterAssigments/Sidepanel'
import Loading from 'modules/finance/components/Loading'
import useCurrentCostCentersAssignments from 'modules/finance/hooks/useCurrentCostCenterAssigments'
import useTrackEmployeeChanges from 'modules/finance/hooks/useTrackEmployeeChanges'

import DimensionsAlertModal from '../../components/Dimensions/DimensionsAlertModal'
import { EmptyState } from './EmptyState'

interface Props {
  employeeId: number
  employeeFullName: string
  managerId: number | null
  locationId: number | null
  teamIdsSignature?: string
}

const EmployeeCostCentersWithHistory = ({
  employeeId,
  employeeFullName,
  managerId,
  locationId,
  teamIdsSignature = '',
}: Props) => {
  const {
    isLoading,
    canSeeCostCenterMemberships,
    canManageCostCenterMemberships,
    assignments,
    hasCostCenters,
    refetch,
  } = useCurrentCostCentersAssignments({
    resourceId: employeeId,
  })

  const { legalEntities } = useLegalEntities()

  const legalEntityIds = legalEntities.map((legalEntity) => legalEntity.id)

  const hasAssignments = assignments.length > 0

  const { navigation } = useNavigation()

  const [manageDeleteCostCenterAssignment, setManageDeleteCostCenterAssignment] = useState(false)
  const [manageCostCentersSidepanelVisible, setManageCostCentersSidepanelVisible] = useState(false)
  const [costCentersChangedAlertModalVisible, setCostCentersChangedAlertModalVisible] =
    useState(false)
  const [alertDescription, setAlertDescription] = useState('')
  const [editableAssignmentId, setEditableAssignmentId] = useState<number | undefined>(undefined)

  const assignmentsIds = assignments.map((assignment) => assignment.id)
  const currentAssignment = assignments.filter((assignment) =>
    moment(assignment.startDate).isSameOrBefore(moment())
  )

  const showAlert = (description: string) => {
    setAlertDescription(description)
    setCostCentersChangedAlertModalVisible(true)
  }

  const onClickCostCentersModal = () => {
    setManageCostCentersSidepanelVisible(true)
    track(trackingEvents.accounting.goToCostCenterManageEmployees)
  }

  const onEditAssignment = () => {
    setManageCostCentersSidepanelVisible(true)
    setEditableAssignmentId(currentAssignment[0]?.id)
  }

  const onCloseManageCostCentersSidepanel = () => {
    setEditableAssignmentId(undefined)
    setManageCostCentersSidepanelVisible(false)
  }

  const onCloseCostCentersAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
  }

  const handleOnSubmitAlertModal = () => {
    setCostCentersChangedAlertModalVisible(false)
    setManageCostCentersSidepanelVisible(true)
  }

  const onClickDeleteOption = () => {
    setManageDeleteCostCenterAssignment(true)
  }

  useTrackEmployeeChanges({
    employeeId,
    managerId,
    locationId,
    teamIdsSignature,
    hasAssignments,
    onChange: showAlert,
  })

  const translations = canManageCostCenterMemberships
    ? {
        description: i18n.t('finance.employee_cost_centers_info.description'),
        button: i18n.t('finance.cost_centers.assignments.manage_button'),
      }
    : {
        description: i18n.t('finance.employee_cost_centers_info.description_no_manage'),
        button: i18n.t('finance.cost_centers.assignments.view_assignments_button'),
      }

  return (
    <Loading loading={isLoading}>
      {canSeeCostCenterMemberships && (
        <>
          <Section
            illustration="money-bag"
            title={i18n.t('finance.employee_cost_centers_info.title')}
            description={translations.description}
            action={
              hasCostCenters
                ? {
                    label: translations.button,
                    onClick: onClickCostCentersModal,
                  }
                : undefined
            }
            {...(canManageCostCenterMemberships && {
              link: {
                href: navigation.settings.finance.costCenters.fullPath(),
                label: i18n.t('finance.employee_cost_centers_info.go_to'),
              },
            })}
          >
            <Box paddingLeft="s64">
              {hasCostCenters ? (
                <Box
                  border={{
                    all: {
                      style: 'solid',
                      width: 's1',
                      color: 'grey400',
                    },
                  }}
                  borderRadius={{
                    all: 'abs008',
                  }}
                  paddingX="s24"
                  paddingY="s20"
                >
                  {assignments.length > 0 ? (
                    <AssignmentList
                      assignments={currentAssignment ? currentAssignment : []}
                      {...(canManageCostCenterMemberships && {
                        options: { onEdit: onEditAssignment, onDelete: onClickDeleteOption },
                      })}
                    />
                  ) : (
                    <EmptyState />
                  )}
                </Box>
              ) : (
                <Alert color="warning">
                  <Box flexDirection="column" gap="s16">
                    <Text.Normal size="300" weight="medium">
                      {i18n.t('finance.employee_cost_centers_info.tip.title')}
                    </Text.Normal>
                    {hasCostCenters && (
                      <Link.Router to={navigation.settings.finance.costCenters.fullPath()}>
                        {i18n.t('finance.employee_cost_centers_info.tip.link')}
                      </Link.Router>
                    )}
                  </Box>
                </Alert>
              )}
            </Box>
          </Section>
          <CostCenterAssignmentsSidepanel
            title={i18n.t(
              'finance.employee_dimensions_info.modals.set_assignments.assignment_title',
              {
                employeeName: employeeFullName,
              }
            )}
            legalEntityIds={legalEntityIds}
            resourceId={employeeId}
            open={manageCostCentersSidepanelVisible}
            onSubmit={() => refetch()}
            onDelete={() => refetch()}
            onClose={onCloseManageCostCentersSidepanel}
            withAssignmentIdOpened={editableAssignmentId}
          />

          {costCentersChangedAlertModalVisible && (
            <DimensionsAlertModal
              description={alertDescription}
              onSubmit={handleOnSubmitAlertModal}
              onCloseDimensionsAlertModal={onCloseCostCentersAlertModal}
            />
          )}

          {manageDeleteCostCenterAssignment && (
            <DeleteCostCenterAssignmentModal
              ids={assignmentsIds}
              onClose={() => {
                setManageDeleteCostCenterAssignment(false)
              }}
              onSubmit={async () => {
                await refetch()

                setManageDeleteCostCenterAssignment(false)
              }}
            />
          )}
        </>
      )}
    </Loading>
  )
}

export default observer(EmployeeCostCentersWithHistory)
